import type {
  CtaBlockPayload,
  FeatureGridBlockPayload,
  HeroBlockPayload,
  LogoStripBlockPayload,
} from '@opinix-cloud/shared';
import { Hero } from '../sections/Hero';
import { TrustStrip } from '../sections/TrustStrip';
import { Platform } from '../sections/Platform';
import { Verticals } from '../sections/Verticals';
import { Features } from '../sections/Features';
import { Capabilities } from '../sections/Capabilities';
import { Deployment } from '../sections/Deployment';
import { FinalCTA } from '../sections/FinalCTA';
import { getBlockPayload } from '../lib/content';
import { useContentSurface } from '../lib/useContentSurface';

interface LandingPageProps {
  onRequestDemo: () => void;
}

/**
 * The original single-page marketing site, now mounted at `/` under the
 * react-router shell. All in-page anchors (#platform, #verticals, etc.)
 * continue to work exactly as before.
 *
 * Copy now flows through the marketing-pages CMS — useContentSurface
 * reads localStorage cache + the /api/content/marketing_landing fetch
 * and falls back to bundled defaults so a fresh deploy renders
 * byte-for-byte identically.
 */
export function LandingPage({ onRequestDemo }: LandingPageProps): JSX.Element {
  const blocks = useContentSurface('marketing_landing');
  const get = <T,>(key: string): T =>
    getBlockPayload<T>(blocks, 'marketing_landing', key);

  return (
    <main>
      <Hero
        onRequestDemo={onRequestDemo}
        content={get<HeroBlockPayload>('hero')}
      />
      <TrustStrip content={get<LogoStripBlockPayload>('trust_strip')} />
      <Platform content={get<FeatureGridBlockPayload>('platform')} />
      <Verticals content={get<FeatureGridBlockPayload>('verticals')} />
      <Features content={get<FeatureGridBlockPayload>('features')} />
      <Capabilities content={get<FeatureGridBlockPayload>('capabilities')} />
      <Deployment content={get<FeatureGridBlockPayload>('deployment')} />
      <FinalCTA
        onRequestDemo={onRequestDemo}
        content={get<CtaBlockPayload>('final_cta')}
      />
    </main>
  );
}
